import React, { useRef, useState } from 'react';
import { LS } from '../../utils/storage';
import { toast } from '../Toast';
import { playNotificationSound, getAudioCtx } from '../../utils/audio';
import { pushSupport, enablePush, disablePush, sendTestPush, hasNotificationPermission } from '../../utils/push';
import { DeviceIcon } from '../PresenceIcons';
import { deviceStatusText } from '../../utils/presence';
import { Icon } from '../Icon';
import { Switch } from './Popover';

// 채팅 화면 전용 상단 버튼: 받은 멘션 / 접속자
export function ChatNavActions({ isMember, unreadNotifCount, onOpenNotifications, membersOpen, onToggleMembers }) {
    return (
        <>
            {isMember && (
                <button className="nav-icon-btn" onClick={onOpenNotifications} title="받은 멘션" aria-label="받은 멘션">
                    <Icon name="at" />{unreadNotifCount > 0 && <span className="nav-badge">{unreadNotifCount > 99 ? '99+' : unreadNotifCount}</span>}
                </button>
            )}
            <button className={`nav-icon-btn${membersOpen ? ' active' : ''}`} onClick={onToggleMembers} title="접속자 목록" aria-label="접속자 목록" aria-pressed={membersOpen}>
                <Icon name="users" />
            </button>
        </>
    );
}

// 계정 메뉴 → "알림·소리" 화면: 내 상태 · 푸시 · 앱이 열려 있을 때 알림 · 소리
export function NotificationSettings({
    notifyEnabled, setNotify, mentionNotifyEnabled, setMentionNotify,
    messageSoundEnabled, setMessageSound, mentionSoundEnabled, setMentionSound,
    isMember, pushOn, setPushOn, myPresence,
}) {
    const [volume, setVolume] = useState(() => LS.getFloat('sound_volume', 0.5));
    const previewTimer = useRef(null);
    const [pushBusy, setPushBusy] = useState(false);
    const support = pushSupport();
    // 알림 권한이 아직 없으면, 스위치를 켤 때 권한을 물어본다는 것을 미리 알려 준다
    const needsPermission = support.supported && !support.denied && !hasNotificationPermission();

    const togglePush = async (checked) => {
        setPushBusy(true);
        try {
            if (checked) {
                await enablePush();
                setPushOn(true);
                toast.success('푸시 알림을 켰습니다. 앱을 닫아도 멘션 알림이 옵니다.');
            } else {
                await disablePush();
                setPushOn(false);
            }
        } catch (err) {
            setPushOn(false);
            toast.error(err.message || '푸시 알림을 켜지 못했습니다.');
        } finally { setPushBusy(false); }
    };
    const testPush = async () => {
        try {
            const r = await sendTestPush();
            if (r.ok) toast.info('테스트 알림을 보냈습니다. 앱을 잠시 닫아 두면 알림으로 보입니다.');
            else toast.error('이 기기로 알림을 보내지 못했습니다. 푸시 알림을 껐다 다시 켜 보세요.');
        } catch (err) { toast.error(err.message); }
    };

    const setDesktopNotify = async (type, checked) => {
        const apply = (value) => {
            if (type === 'general') { setNotify(value); LS.set('notify_general', value); }
            else { setMentionNotify(value); LS.set('notify_mention', value); }
        };
        if (!checked) return apply(false);
        if (!('Notification' in window)) { toast.error('이 브라우저는 알림을 지원하지 않습니다. 앱으로 설치 후 사용하세요.'); return apply(false); }
        if (Notification.permission === 'denied') { toast.error('알림이 차단되어 있습니다. 주소창 자물쇠 → 알림 → 허용 후 새로고침하세요.'); return apply(false); }
        if (Notification.permission !== 'granted' && (await Notification.requestPermission()) !== 'granted') {
            toast.error('알림 권한이 거부되었습니다.');
            return apply(false);
        }
        apply(true);
    };
    const setSound = (type, checked) => {
        getAudioCtx();
        if (checked) playNotificationSound(type);
        if (type === 'message') { setMessageSound(checked); LS.set('sound_message', checked); }
        else { setMentionSound(checked); LS.set('sound_mention', checked); }
    };
    const changeVolume = (value) => {
        setVolume(value);
        LS.set('sound_volume', value);
        clearTimeout(previewTimer.current);
        previewTimer.current = setTimeout(() => playNotificationSound('message'), 250);
    };

    return (
        <div className="notify-settings" onPointerDown={getAudioCtx}>
            {myPresence && (
                <div className="my-presence">
                    <DeviceIcon device={myPresence.device} status={myPresence.dnd ? 'dnd' : myPresence.active ? 'online' : 'idle'} active={myPresence.active} size={18} />
                    <div>
                        <b>내 상태: {deviceStatusText({ device: myPresence.device, status: myPresence.dnd ? 'dnd' : myPresence.active ? 'online' : 'idle', active: myPresence.active })}</b>
                        <small>{myPresence.dnd ? '알림을 모두 꺼 두어 "방해 금지"로 보입니다. 아래 알림이나 소리를 하나라도 켜면 온라인으로 바뀝니다.' : '이 창을 보고 있으면 온라인, 다른 창을 보면 자리 비움으로 보입니다.'}</small>
                    </div>
                </div>
            )}
            {isMember && (
                <>
                    <div className="nav-popover-title">푸시 알림 (앱을 닫아도 받기)</div>
                    <Switch label={pushBusy ? '설정 중...' : '휴대폰·PC 푸시 알림'} checked={pushOn} disabled={pushBusy || !support.supported || support.denied}
                        onChange={togglePush} />
                    {!support.supported || support.denied
                        ? <p className="nav-hint">{support.reason}</p>
                        : <p className="nav-hint">
                            {!pushOn && needsPermission && '켜면 이 브라우저가 알림 권한을 물어봅니다. 허용해야 켜집니다. '}
                            채널마다 🔔 버튼으로 "모든 메시지 / 멘션만 / 끄기"를 고를 수 있어요.
                            {pushOn && <> <button className="nav-link-btn" onClick={testPush}>테스트 알림</button></>}
                          </p>}
                </>
            )}
            <div className="nav-popover-title">앱이 열려 있을 때</div>
            <Switch label="모든 새 메시지" checked={notifyEnabled} onChange={v => setDesktopNotify('general', v)} />
            <Switch label="나를 멘션한 메시지" checked={mentionNotifyEnabled} onChange={v => setDesktopNotify('mention', v)} />
            <div className="nav-popover-title">소리</div>
            <Switch label="새 메시지" checked={messageSoundEnabled} onChange={v => setSound('message', v)} />
            <Switch label="멘션" checked={mentionSoundEnabled} onChange={v => setSound('mention', v)} />
            <label className="nav-volume">
                <span>볼륨</span>
                <input type="range" min="0" max="1" step="0.05" value={volume} onChange={e => changeVolume(parseFloat(e.target.value))} />
                <span className="nav-volume-value">{Math.round(volume * 100)}%</span>
            </label>
        </div>
    );
}
